/**
 * Provider self-onboarding API client.
 *
 * Endpoints (D9 / spec Section 2 Real Negotiation Engine):
 *   POST /api/providers/signup
 *   POST /api/providers/{id}/request-otp
 *   POST /api/providers/{id}/verify-otp
 *   GET  /api/providers/{id}
 */

function apiBaseUrl(): string {
  if (typeof window === "undefined") {
    return process.env.API_INTERNAL_URL ?? process.env.API_URL ?? "http://localhost:8000";
  }
  return process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
}

export type ProviderStatus = "self_declared" | "verified" | "delisted";

export interface SignupBody {
  business_name: string;
  contact_name: string;
  email: string;
  trade_type: string;
  service_area: string[];
  mobile?: string;
  capacity_per_week?: number;
  discount_commitment?: string;
  licence_number?: string;
  country_code?: string;
}

export interface SignupResponse {
  provider_id: string;
  email_otp_sent: boolean;
  status: ProviderStatus;
}

export interface PublicProviderProfile {
  id: string;
  business_name: string;
  contact_name: string;
  trade_type: string;
  service_area: string[];
  capacity_per_week: number | null;
  discount_commitment: string | null;
  licence_number: string | null;
  status: ProviderStatus;
  country_code: string;
  created_at: string;
}

export class ProvidersApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, detail: unknown, message: string) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

async function _post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${apiBaseUrl()}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text();
    }
    throw new ProvidersApiError(res.status, detail, `POST ${path} returned ${res.status}`);
  }
  return (await res.json()) as T;
}

export async function signup(body: SignupBody): Promise<SignupResponse> {
  return _post<SignupResponse>("/api/providers/signup", body);
}

export async function requestOtp(
  providerId: string,
  channel: "email" | "mobile" = "email",
): Promise<void> {
  await _post(`/api/providers/${providerId}/request-otp`, { channel });
}

export async function verifyOtp(
  providerId: string,
  code: string,
  channel: "email" | "mobile" = "email",
): Promise<PublicProviderProfile> {
  return _post<PublicProviderProfile>(
    `/api/providers/${providerId}/verify-otp`,
    { channel, code },
  );
}

export async function getProvider(providerId: string): Promise<PublicProviderProfile> {
  const res = await fetch(`${apiBaseUrl()}/api/providers/${providerId}`, {
    method: "GET",
    cache: "no-store",
  });
  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text();
    }
    throw new ProvidersApiError(
      res.status,
      detail,
      `GET /api/providers/${providerId} returned ${res.status}`,
    );
  }
  return (await res.json()) as PublicProviderProfile;
}
